import {Random} from "@dp-instant/utils";
import {TypesGame} from "@game/TypesGame";
import {math} from "@game/coregame/helpers";
import {
    BotPreset0Vegetable,
    BotPreset1Easy,
    BotPreset2Medium,
    BotPreset3UpperMedium,
    BotPreset4Hard,
    BotPreset5Final,
    BotPreset6FinalAgressive
} from "@game/coregame/model/BotPresets";
import {inject, injectable} from "inversify";
import {IBotConfig} from "../interfaces/IBotConfig";
import {CookieModel} from "./CookieModel";
import {GameModel} from "./GameModel";
import {apiClient} from "@game/api/ApiClient";

/** Indexed array of all bot presets for remote config lookup */
const ALL_PRESETS: IBotConfig[] = [
    BotPreset0Vegetable,    // 0
    BotPreset1Easy,         // 1
    BotPreset2Medium,       // 2
    BotPreset3UpperMedium,  // 3
    BotPreset4Hard,         // 4
    BotPreset5Final,        // 5
    BotPreset6FinalAgressive // 6
];

type PresetWeight = [Preset: IBotConfig, Weight: float];
type Presets = [WinsToLevelUp: int, LosesToLevelDown: int, Preset: PresetWeight[]];

/**
 * @description Incapsulates bot selection logic
 */

@injectable()
export class BotPresetsModel {
    private _startBot: IBotConfig = BotPreset0Vegetable;
    private _currentPresetName?: string = "";
    private _lowLevelPresetEveryFights: int = 3;

    private _presets: Presets[] = [
        [3, 0, [[BotPreset1Easy, 1]]],
        [2, 1, [[BotPreset2Medium, 1]]],
        [3, 1, [[BotPreset3UpperMedium, 1]]],
        [3, 1, [[BotPreset4Hard, 1]]],
        [3, 1, [[BotPreset6FinalAgressive, 1]]]
    ];

    @inject(TypesGame.model)
    protected model: GameModel;

    @inject(TypesGame.cookieModel)
    private cookie: CookieModel;

    public selectPreset(): IBotConfig {
        // A/B: early_bot_sequence overrides preset selection for first level fights
        if (__SOCIAL__ === "tg" && this.cookie.absoluteLevelNum === 1) {
            const seq = apiClient.configValue("early_bot_sequence", null) as number[] | null;
            if (seq && Array.isArray(seq)) {
                const idx = this.cookie.currentStage;
                if (idx < seq.length && seq[idx] >= 0 && seq[idx] < ALL_PRESETS.length) {
                    const preset = ALL_PRESETS[seq[idx]];
                    this._currentPresetName = preset.name;
                    return preset;
                }
            }
        }

        if (this.cookie.absoluteLevelNum === 1 && this.cookie.currentStage < 2) {
            this._currentPresetName = this._startBot.name;
            return this._startBot;
        }

        if (this.isLowLevelFight) {
            var prevLevel = this.getPrevLevel(this.cookie.botLevel);
            const [, , Preset] = this._presets[prevLevel];
            const currentPreset = this.selectRandom(Preset);
            this._currentPresetName = currentPreset.name;
            return currentPreset;
        }
        const [, , Preset] = this._presets[this.cookie.botLevel];
        const currentPreset = this.selectRandom(Preset);
        this._currentPresetName = currentPreset.name;
        return currentPreset;
    }

    private selectRandom(presets: PresetWeight[]): IBotConfig {
        const [Preset] = Random.from(presets);
        return Preset;
        // const weights = presets.map(preset => preset[1]);
        // const index = RandomExtensions.IndexByWeight(weights);
        // const [Preset] = presets[index];
        // return Preset;
    }

    public updateBotLevel(isCurrentResultWin: boolean) {
        if (isCurrentResultWin) {
            if (this.isLowLevelFight) {
                this.cookie.fightsPassed++;
                return;
            }

            this.cookie.fightsPassed++;
            this.cookie.wins++;
            //WinsToLevelUp
            if (this.cookie.wins > this._presets[this.cookie.botLevel][0]) {
                this.cookie.wins = 0;
                this.cookie.loses = 0;
                this.cookie.fightsPassed = 0;
                this.cookie.botLevel = Math.min(this.cookie.botLevel + 1, this._presets.length - 1);
            }
        } else {
            if (this.isLowLevelFight) {
                this.cookie.fightsPassed++;
                return;
            }

            this.cookie.fightsPassed++;
            this.cookie.loses++;
            //LosesToLevelDown
            if (this.cookie.loses > this._presets[this.cookie.botLevel][1]) {
                this.cookie.wins = 0;
                this.cookie.loses = 0;
                this.cookie.fightsPassed = 0;
                this.cookie.botLevel = Math.max(this.cookie.botLevel - 1, 0);
            }
        }
    }

    private getPrevLevel(level: number): number {
        return math.clamp(level - 1, 0, this._presets.length - 1);
    }

    get isLowLevelFight(): bool {
        return (
            this.cookie.fightsPassed % this._lowLevelPresetEveryFights == 1 &&
            this.cookie.botLevel > 0
        );
    }

    get currentPresetName(): string {
        return this._currentPresetName || "";
    }
}
